<template>
  <div class="public-dashboard-page">
    <header class="public-dashboard-header">
      <div class="public-dashboard-brand">
        <img class="public-dashboard-brand__logo" :src="cinemaLogo" alt="CINEIGA">
        <span class="public-dashboard-brand__badge">無料版</span>
      </div>

      <div class="public-dashboard-controls">
        <button type="button" class="picker area-picker" @click="openUpgradeDialog('region')">
          <span class="picker-text">全国</span>
          <span class="triangle" />
        </button>
        <button type="button" class="picker date-picker" @click="openCalendar">
          <span class="picker-text">{{ formattedDate }}</span>
          <span class="triangle" />
        </button>
      </div>
    </header>

    <template v-if="unlocked">
      <MovieContent
        v-show="!showCalendar"
        public-mode
        :selected-date="today"
        :paused="showCalendar"
        :force-refresh-key="0"
        :soft-refresh-key="0"
        @change-date="onChangeDateAttempt"
      />

      <div v-if="showCalendar" class="calendar-container">
        <DashboardCalendar
          :select-date="todayStr"
          :select-min-date="minDate"
          :select-max-date="todayStr"
          @back="onCalendarBack"
        />
      </div>

      <UpgradeDialog v-model="upgradeDialogVisible" :scenario="upgradeDialogScenario" />
    </template>

    <!-- 臨時アクセス制限：パスワード認証前は背景をぼかし、MovieContent を一切マウントしない（＝バックエンドへのリクエストを一切発生させない） -->
    <div v-if="!unlocked" class="gate-overlay">
      <form class="gate-card" @submit.prevent="handleUnlock">
        <div class="gate-card__title">パスワードを入力してください</div>
        <input
          v-model="gatePassword"
          type="password"
          class="gate-card__input"
          placeholder="Password"
          autofocus
          autocomplete="off"
          @input="gateError = ''"
        >
        <button type="submit" class="gate-card__button">確認</button>
        <div v-if="gateError" class="gate-card__error">{{ gateError }}</div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import dayjs from 'dayjs'
import 'dayjs/locale/ja'
import DashboardCalendar from '../components/dashboard-calendar.vue'
import MovieContent from '../components/movie-content.vue'
import UpgradeDialog from './components/upgrade-dialog.vue'
import cinemaLogo from '@/assets/logo/cinema_logo.png'

dayjs.locale('ja')

// 臨時アクセス制限（ハードコードパスワード）：本番の権限体系が入るまでの暫定措置
const GATE_PASSWORD = 'cineiga2026'
const GATE_STORAGE_KEY = 'public_dashboard_unlocked'
const unlocked = ref(sessionStorage.getItem(GATE_STORAGE_KEY) === '1')
const gatePassword = ref('')
const gateError = ref('')

function handleUnlock() {
  if (gatePassword.value === GATE_PASSWORD) {
    unlocked.value = true
    gateError.value = ''
    sessionStorage.setItem(GATE_STORAGE_KEY, '1')
  } else {
    gateError.value = 'パスワードが正しくありません'
    gatePassword.value = ''
  }
}

// 無料版：閲覧できるのは常に「本日・全国」固定。selectedDate は変更しない
const today = ref(dayjs())
const todayStr = computed(() => today.value.format('YYYY-M-D'))
const minDate = '2011-1-1'

const showCalendar = ref(false)
const upgradeDialogVisible = ref(false)
const upgradeDialogScenario = ref('date')

const formattedDate = computed(() => today.value.format('YYYY年M月D日'))

function openCalendar() {
  showCalendar.value = true
}

function openUpgradeDialog(scenario) {
  upgradeDialogScenario.value = scenario
  upgradeDialogVisible.value = true
}

// 日历中选择非当天日期（含区间模式）：不切换日期、不发起查询，改为提示付费升级
// dashboard-calendar.vue の @back は { start, end } を渡す（純粋な「戻る」時は引数なし）
function onCalendarBack(picked) {
  showCalendar.value = false
  if (!picked?.start || !picked?.end) return // 純粋な「戻る」で閉じるだけ

  const start = dayjs(picked.start)
  const end = dayjs(picked.end)
  const isValidSingleToday = start.isValid() && end.isValid()
    && start.startOf('day').isSame(end.startOf('day'), 'day')
    && start.startOf('day').isSame(today.value.startOf('day'), 'day')
  if (!isValidSingleToday) {
    openUpgradeDialog('date')
  }
}

// movie-content.vue 内のモバイル「前日」ボタン経由：同様に当日以外は拒否してダイアログ表示
function onChangeDateAttempt(date) {
  const next = dayjs(date)
  if (!next.isValid() || !next.startOf('day').isSame(today.value.startOf('day'))) {
    openUpgradeDialog('date')
  }
}
</script>

<style lang="less" scoped>
.public-dashboard-page {
  /* movie-content テーマ変数：.dashboard-page と同様にここで定義し MovieContent に継承させる */
  --mc-bg: var(--bg-panel, #fff);
  --mc-bg-soft: color-mix(in srgb, var(--brand-color, #f9da49) 6%, var(--bg-panel, #fff));
  --mc-row: var(--bg-panel, #fff);
  --mc-row-alt: color-mix(in srgb, var(--brand-color, #f9da49) 5%, var(--bg-panel, #fff));
  --mc-row-selected: color-mix(in srgb, var(--brand-color, #f9da49) 22%, var(--bg-panel, #fff));
  --mc-row-hover: color-mix(in srgb, var(--brand-color, #f9da49) 12%, var(--bg-panel, #fff));
  --mc-text: var(--text-primary, #000);
  --mc-text-muted: var(--text-secondary, #4a4a4a);
  --mc-accent: var(--brand-color, #f9da49);
  /* 興行収入(AI推測値) / 総興行収入 の強調数値：テーマ主色から派生した偏移色（濃いめの赤） */
  --mc-accent-strong: var(--el-color-primary-dark-2, #a31d15);
  --mc-border: var(--border-color, #e5e5e5);
  --mc-scrollbar-thumb: color-mix(in srgb, var(--brand-color, #f9da49) 38%, #b8bdc4);
  --mc-scrollbar-thumb-hover: color-mix(in srgb, var(--brand-color, #f9da49) 58%, #9aa0a6);
  --mc-scrollbar-thumb-active: color-mix(in srgb, var(--brand-color, #f9da49) 72%, #858b92);

  box-sizing: border-box;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100vh;
  height: 100dvh;
  min-height: 0;
  padding: 12px 16px 20px;
  background: var(--bg-body, #fff);
  overflow: hidden;
}

/* 暗色模式：.dashboard-page と同様に色味を調整 */
html.dark .public-dashboard-page {
  /* 深色模式：強調数値はテーマ赤の明るめ派生色（暗背景で視認性確保） */
  --mc-accent-strong: var(--el-color-primary-light-3, #db665f);
  --mc-bg-soft: color-mix(in srgb, var(--brand-color, #f9da49) 8%, var(--bg-panel, #20242b));
  --mc-row: var(--bg-panel, #20242b);
  --mc-row-alt: color-mix(in srgb, #ffffff 4.5%, var(--bg-panel, #20242b));
  --mc-row-selected: color-mix(in srgb, var(--brand-color, #f9da49) 18%, var(--bg-panel, #20242b));
  --mc-row-hover: color-mix(in srgb, var(--brand-color, #f9da49) 10%, var(--bg-panel, #20242b));
  --mc-scrollbar-thumb: color-mix(in srgb, var(--brand-color, #f9da49) 28%, #6b717a);
  --mc-scrollbar-thumb-hover: color-mix(in srgb, var(--brand-color, #f9da49) 48%, #8a9099);
  --mc-scrollbar-thumb-active: color-mix(in srgb, var(--brand-color, #f9da49) 62%, #a0a6ad);
}

.public-dashboard-header {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  gap: 12px;
  height: 56px;
  padding: 0 16px;
  font-size: 14px;
  background: var(--bg-panel);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  box-shadow: var(--shadow-sm);
}

.public-dashboard-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.public-dashboard-brand__logo {
  height: 34px;
  width: auto;
  object-fit: contain;
}

.public-dashboard-brand__badge {
  flex-shrink: 0;
  padding: 2px 8px;
  border: 1px solid color-mix(in srgb, var(--brand-color) 45%, var(--border-color));
  border-radius: 10px;
  background: color-mix(in srgb, var(--brand-color) 18%, var(--bg-panel));
  color: var(--text-primary);
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.public-dashboard-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.picker {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid color-mix(in srgb, var(--brand-color) 45%, var(--border-color));
  border-radius: 16px;
  background: color-mix(in srgb, var(--brand-color) 18%, var(--bg-panel));
  color: var(--text-primary);
  font-size: 14px;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: color-mix(in srgb, var(--brand-color) 28%, var(--bg-panel));
    border-color: var(--brand-color);
  }
}

.picker-text {
  overflow: hidden;
  text-overflow: ellipsis;
}

.triangle {
  display: inline-block;
  width: 6px;
  height: 6px;
  margin-top: -2px;
  border-top: 1px solid var(--text-primary);
  border-left: 1px solid var(--text-primary);
  transform: scaleY(-1) rotate(45deg);
  vertical-align: middle;
}

.calendar-container {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background-color: color-mix(in srgb, #000 35%, transparent);
  backdrop-filter: blur(2px);
}

.gate-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: color-mix(in srgb, #000 25%, transparent);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
}

.gate-card {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  width: min(320px, 86vw);
  padding: 28px 24px;
  background: var(--bg-panel, #fff);
  border: 1px solid var(--border-color, #e5e5e5);
  border-radius: 12px;
  box-shadow: var(--shadow-md, 0 8px 24px rgba(0, 0, 0, 0.15));
}

.gate-card__title {
  margin-bottom: 4px;
  color: var(--text-primary, #000);
  font-size: 15px;
  font-weight: 600;
  text-align: center;
}

.gate-card__input {
  height: 40px;
  padding: 0 12px;
  border: 1px solid var(--border-color, #e5e5e5);
  border-radius: 8px;
  background: var(--bg-body, #fff);
  color: var(--text-primary, #000);
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: var(--brand-color, #f9da49);
  }
}

.gate-card__button {
  height: 40px;
  border: none;
  border-radius: 8px;
  background: var(--brand-color, #f9da49);
  color: #000;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.85;
  }
}

.gate-card__error {
  color: var(--el-color-danger, #f56c6c);
  font-size: 12px;
  text-align: center;
}

@media (max-width: 992px) {
  .public-dashboard-page {
    height: calc(100vh - 12px);
    height: calc(100dvh - 12px);
    min-height: 0;
    padding: 8px 10px 12px;
    gap: 8px;
    overflow: hidden;
  }

  .public-dashboard-header {
    height: auto;
    min-height: 48px;
    padding: 10px;
    flex-wrap: wrap;
    border-radius: 12px;
  }
}

@media (max-width: 768px) {
  .public-dashboard-brand__badge {
    display: none;
  }

  .picker-text {
    max-width: 80px;
  }
}
</style>
